import { ChatError, type ChatAttachment, type FinalizedAttachment } from '../types'
import { RAY_ID_UNAVAILABLE } from '../engine/errors'

/**
 * Resolve the submit input to the actual prompt text: literal strings win,
 * function inputs transform the current composer text, and null/undefined
 * mean "no prompt" (continuation kicks). Blank input collapses to undefined.
 */
export function reconcileChatInput(
  input: string | ((prevValue: string) => string) | undefined | null,
  currentInput: string | undefined,
): string | undefined {
  let chatInput: string | undefined
  if (typeof input === 'string') {
    chatInput = input
  } else if (typeof input === 'function') {
    chatInput = input(currentInput ?? '')
  }
  chatInput = chatInput?.trim()
  return chatInput === '' ? undefined : chatInput
}

export interface FinalizeAttachmentsResult {
  attachedContext: Array<FinalizedAttachment>
  error?: ChatError
  /** Records what this send carried, once the backend has taken the turn. */
  onTurnAccepted?: () => void
}

/**
 * Resolve a submission's finalized attachments. Priority:
 * 1) pre-finalized `attachedContext` (bypasses the finalize step; any extra
 *    pending attachments are finalized and appended)
 * 2) pending or finalized attachments through `finalizeAttachments`
 * A promptless submission discards context entirely — the backend 400s when
 * context is attached to a null prompt. Upload failures land on `uploadError`;
 * this never throws.
 */
export async function resolveAttachedContext(params: {
  chatInput: string | undefined
  includedAttachments: Array<ChatAttachment>
  preFinalized: Array<FinalizedAttachment> | undefined
  finalizeAttachments: (attachments: Array<ChatAttachment>, chatId: string) => Promise<FinalizeAttachmentsResult>
  chatId: string
  userMessageId: string
}): Promise<{
  attachedContext: Array<FinalizedAttachment>
  uploadError?: ChatError
  onTurnAccepted?: () => void
}> {
  const { chatInput, includedAttachments, preFinalized } = params
  try {
    if (!chatInput) {
      return { attachedContext: [] }
    }
    if (preFinalized && preFinalized.length > 0) {
      if (includedAttachments.length === 0) {
        return { attachedContext: preFinalized }
      }
      const result = await params.finalizeAttachments(includedAttachments, params.chatId)
      return {
        attachedContext: [...preFinalized, ...result.attachedContext],
        uploadError: result.error,
        onTurnAccepted: result.onTurnAccepted,
      }
    }
    const result = await params.finalizeAttachments(includedAttachments, params.chatId)
    return { attachedContext: result.attachedContext, uploadError: result.error, onTurnAccepted: result.onTurnAccepted }
  } catch (err: unknown) {
    console.error('Error finalizing attachments:', err)
    const uploadError =
      err instanceof ChatError
        ? err
        : new ChatError(String(err), {
            chatId: params.chatId,
            userMessageId: params.userMessageId,
            assistantMessageId: null,
            errorCode: 'attachment-upload-error',
            rayId: RAY_ID_UNAVAILABLE,
          })
    return { attachedContext: [], uploadError }
  }
}

// `useUploadFile` prefixes the backend reason with this when it throws.
const PREPARE_PREFIX = 'Failed to prepare file for upload:'

const GENERIC_UPLOAD_FAILURE =
  'File upload failed. You may have reached the file size limit or the file is in an unsupported format.'

/**
 * User-facing copy for an attachment upload failure: strip the internal
 * `PREPARE_PREFIX` so only the backend-provided reason shows (e.g. "File size
 * exceeds …"), falling back to generic copy when nothing is left.
 */
export function attachmentErrorMessage(uploadError: ChatError): string {
  const message = uploadError.message.trim()
  const reason = (message.startsWith(PREPARE_PREFIX) ? message.slice(PREPARE_PREFIX.length) : message).trim()
  return reason || GENERIC_UPLOAD_FAILURE
}
